import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  GraduationCap, BookOpen, CheckCircle2, XCircle, AlertTriangle,
  Calendar, LogOut, User, Clock, X, CalendarDays, Video, Radio, Lock,
  MessageCircle, CreditCard, Plus, RefreshCcw, Megaphone
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import ReferralCard from "@/components/ReferralCard";
import MessagingDialog from "@/components/MessagingDialog";

const API = "";

const STATE_CONFIG = {
  live: { label: "LIVE", color: "bg-red-100 text-red-700 border border-red-300", icon: Radio, pulse: true },
  upcoming: { label: "Upcoming", color: "bg-blue-100 text-blue-700", icon: Clock },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  no_show: { label: "No Show", color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  pending_tagging: { label: "Pending Confirmation", color: "bg-slate-100 text-slate-600", icon: Clock },
  cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-500", icon: XCircle },
};

function ClassStateBadge({ state }) {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.upcoming;
  const Icon = cfg.icon;
  return (
    <Badge className={cn("inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1", cfg.color)} data-testid={`state-${state}`}>
      <Icon className={cn("w-3 h-3", cfg.pulse && "animate-pulse")} />
      {cfg.label}
    </Badge>
  );
}

function getStatusBadge(status) {
  const s = status?.toLowerCase() || "";
  if (s === "present") return <Badge className="bg-emerald-100 text-emerald-700">Present</Badge>;
  if (s.includes("no show")) return <Badge className="bg-amber-100 text-amber-700">No Show</Badge>;
  if (s.includes("cancelled by student") || s.includes("cancelled by you")) return <Badge className="bg-slate-100 text-slate-600">Cancelled by You</Badge>;
  if (s.includes("cancelled")) return <Badge className="bg-slate-100 text-slate-500">Cancelled</Badge>;
  if (s.includes("renewed") || s.includes("subscription")) return <Badge className="bg-blue-100 text-blue-700">Renewed</Badge>;
  return <Badge className="bg-slate-100 text-slate-600">{status}</Badge>;
}

export default function StudentDashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState({ open: false, slot: null });
  const [cancelling, setCancelling] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [rescheduleDialog, setRescheduleDialog] = useState({ open: false, slot: null });
  const [referralData, setReferralData] = useState(null);
  const [messagingOpen, setMessagingOpen] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/student-portal/dashboard`, { headers: getAuthHeader() });
      setDashboard(response.data);
      // Fetch payment history
      if (response.data?.student?.id) {
        try {
          const payRes = await axios.get(`${API}/api/payments/history/${response.data.student.id}`);
          setPaymentHistory(payRes.data.items || []);
        } catch {}
      }
    } catch (error) {
      if (error.response?.status === 401) { logout(); return; }
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, logout]);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 30000); // Refresh every 30s for live state updates
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  // Load referral code once
  useEffect(() => {
    axios
      .get(`/api/referrals/my-code`, { headers: getAuthHeader() })
      .then((r) => setReferralData(r.data))
      .catch(() => setReferralData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoinClass = async (slot) => {
    try {
      const res = await axios.post(`${API}/api/student-portal/join-class/${slot.id}`, {}, { headers: getAuthHeader() });
      toast.success(`Joined at ${res.data.joined_at} (Turkey Time)`);
      if (res.data.zoom_link) {
        window.open(res.data.zoom_link, "_blank");
      }
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cannot join class");
    }
  };

  const handleCancelClass = async () => {
    if (!cancelDialog.slot) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/api/student-portal/cancel-class/${cancelDialog.slot.id}`, {}, { headers: getAuthHeader() });
      toast.success("Class cancelled successfully. No credits deducted.");
      setCancelDialog({ open: false, slot: null });
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel class");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-slate-400 text-lg">Loading your dashboard...</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center"><p className="text-slate-500">Unable to load dashboard.</p>
          <Button onClick={fetchDashboard} className="mt-4">Retry</Button>
        </div>
      </div>
    );
  }

  const { student, stats, upcoming_classes = [], class_history = [] } = dashboard;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{student.name}</p>
              <p className="text-xs text-slate-500">Student Portal</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: "Purchased", value: stats.total_classes, icon: GraduationCap, color: "text-indigo-600", bg: "bg-indigo-100" },
            { label: "Completed", value: stats.completed, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-100" },
            { label: "Cancelled", value: stats.cancelled, icon: XCircle, color: "text-slate-500", bg: "bg-slate-100" },
            { label: "No Show", value: stats.no_show, icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100" },
            { label: "Upcoming", value: stats.upcoming, icon: CalendarDays, color: "text-blue-600", bg: "bg-blue-100" },
            { label: "Available", value: stats.available_credits ?? stats.remaining, icon: Calendar, color: "text-white", bg: "bg-emerald-500", cardBg: "bg-gradient-to-br from-emerald-500 to-teal-600 text-white" },
          ].map(({ label, value, icon: Icon, color, bg, cardBg }) => (
            <Card key={label} className={cn("border-0 shadow-md", cardBg || "bg-white")}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-2.5">
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", cardBg ? "bg-white/20" : bg)}>
                    <Icon className={cn("w-4.5 h-4.5", cardBg ? "text-white" : color)} />
                  </div>
                  <div>
                    <p className={cn("text-xl font-bold", cardBg ? "" : color)}>{value}</p>
                    <p className={cn("text-xs", cardBg ? "text-white/80" : "text-slate-500")}>{label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Available vs Reserved info */}
        {stats.reserved_future > 0 && (
          <div className="mb-4 text-xs text-slate-500 flex items-center gap-2 flex-wrap" data-testid="available-info">
            <span>💡 You have <strong>{stats.remaining}</strong> credits total — <strong>{stats.reserved_future}</strong> reserved for upcoming classes — <strong className="text-emerald-700">{stats.available_credits} available</strong> for new bookings.</span>
          </div>
        )}

        {/* Low Credit Alert (uses available_credits, not remaining) */}
        {(stats.available_credits ?? stats.remaining) <= 3 && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3" data-testid="low-credit-alert">
            <div className="flex items-center gap-2 flex-1">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-amber-800 text-sm">
                  {(stats.available_credits ?? stats.remaining) === 0 ? "No available credits for new bookings!" : `Only ${stats.available_credits ?? stats.remaining} available credit${(stats.available_credits ?? stats.remaining) === 1 ? "" : "s"} for new bookings!`}
                </p>
                <p className="text-amber-600 text-xs">{(stats.available_credits ?? stats.remaining) === 0 ? "All your remaining credits are already reserved for upcoming classes. Top up to book more." : "Top up your credits to continue booking classes."}</p>
              </div>
            </div>
            <a href="/subscribe"
              className="inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-10 px-5 text-sm rounded-md flex-shrink-0 transition-colors"
              data-testid="low-credit-renew-btn"
            >
              <CreditCard className="w-4 h-4" /> Renew Subscription
            </a>
          </div>
        )}

        {/* Renew / Top Up Button (always visible) */}
        <div className="mb-6 flex justify-between items-center gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <Link to="/tutors"
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-semibold text-sm h-10 px-5 rounded-md transition-all hover:opacity-90 shadow-md"
              data-testid="browse-tutors-btn"
            >
              <Plus className="w-4 h-4" /> Browse Tutors & Book a Class
            </Link>
            <button
              onClick={() => setMessagingOpen(true)}
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-700 to-blue-900 text-white font-semibold text-sm h-10 px-5 rounded-md transition-all hover:opacity-90 shadow-md"
              data-testid="message-tutor-btn"
            >
              <MessageCircle className="w-4 h-4" /> Message Tutor
            </button>
          </div>
          <a href="/subscribe"
            className="inline-flex items-center gap-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 font-medium text-sm h-9 px-4 rounded-md transition-colors"
            data-testid="topup-credits-btn"
          >
            <CreditCard className="w-4 h-4" /> Renew Subscription
          </a>
        </div>

        {/* Referral Card */}
        {referralData && (
          <div className="mb-6">
            <ReferralCard data={referralData} compact={false} getAuthHeader={getAuthHeader} />
          </div>
        )}

        {/* Validity Date */}
        {student.validity_date && (
          <div className="mb-6 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-blue-700 text-sm">
              <Calendar className="w-4 h-4 inline mr-2" />
              <strong>Package Valid Until:</strong> {format(new Date(student.validity_date), "MMMM d, yyyy")}
            </p>
          </div>
        )}

        {/* Upcoming Classes */}
        <Card className="border-0 shadow-md mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-blue-600" />
              My Classes
              <span className="text-sm font-normal text-slate-400 ml-1">(Turkey Time)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcoming_classes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No upcoming classes scheduled</p>
                <p className="text-sm mt-1">Contact your teacher to book a class</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcoming_classes.map((slot) => {
                  const state = slot.class_state || "upcoming";
                  const isLive = state === "live";
                  const canJoin = slot.can_join;
                  const canCancel = slot.can_cancel && state === "upcoming";

                  return (
                    <div
                      key={slot.id}
                      className={cn(
                        "rounded-xl border p-4 transition-all",
                        isLive ? "border-red-200 bg-red-50/50 shadow-md" : "border-slate-200 bg-white hover:border-slate-300"
                      )}
                      data-testid={`class-card-${slot.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {/* Date + Time */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <ClassStateBadge state={state} />
                            <span className="text-sm font-semibold text-slate-900">
                              {format(new Date(slot.date), "EEE, MMM d, yyyy")}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                              {slot.start_time || slot.time} - {slot.end_time || ""}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-3.5 h-3.5 text-slate-400" />
                              {slot.tutor_name}
                            </span>
                            {slot.duration_minutes && (
                              <span className="text-xs text-slate-400">{slot.duration_minutes} min</span>
                            )}
                          </div>
                          {/* Joined timestamp */}
                          {slot.joined_at && (
                            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Joined at {slot.joined_at} (Turkey Time)
                            </p>
                          )}
                          {/* Cancellation reason (when teacher cancelled) */}
                          {state === "cancelled" && slot.notes && (
                            <div className="mt-2 text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded-md px-2 py-1 flex items-start gap-1.5" data-testid={`cancellation-reason-${slot.id}`}>
                              <Megaphone className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span><strong>Cancellation reason:</strong> {slot.notes}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Join Classroom Button - only when LIVE */}
                          {canJoin && !slot.joined_at && (
                            <Button
                              className="bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg h-10 px-5"
                              onClick={() => handleJoinClass(slot)}
                              data-testid={`join-btn-${slot.id}`}
                            >
                              <Video className="w-4 h-4 mr-2" /> Join Now
                            </Button>
                          )}
                          {canJoin && slot.joined_at && (
                            <a href={slot.zoom_link} target="_blank" rel="noopener noreferrer">
                              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-10 px-5" data-testid={`rejoin-btn-${slot.id}`}>
                                <Video className="w-4 h-4 mr-2" /> Rejoin
                              </Button>
                            </a>
                          )}
                          {isLive && !slot.zoom_link && (
                            <span className="text-xs text-red-500 flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Classroom link not available
                            </span>
                          )}
                          {state === "upcoming" && slot.minutes_until_class <= 5 && (
                            <Button disabled className="h-9 text-xs opacity-60">
                              <Clock className="w-3.5 h-3.5 mr-1" /> Opens soon
                            </Button>
                          )}
                          {(state === "completed" || state === "no_show" || state === "pending_tagging") && (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Class ended
                            </span>
                          )}

                          {/* Cancel + Reschedule Buttons */}
                          {canCancel && state === "upcoming" ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-9"
                                onClick={() => setRescheduleDialog({ open: true, slot })}
                                data-testid={`reschedule-class-${slot.id}`}
                              >
                                <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Reschedule
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9"
                                onClick={() => setCancelDialog({ open: true, slot })}
                                data-testid={`cancel-class-${slot.id}`}
                              >
                                <X className="w-3.5 h-3.5 mr-1" /> Cancel
                              </Button>
                            </>
                          ) : state === "upcoming" && !slot.can_cancel ? (
                            <span className="text-[11px] text-slate-400 flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Cancellation closed
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Class History */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              Class History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {class_history.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No class records yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-slate-700">Date</TableHead>
                      <TableHead className="font-semibold text-slate-700">Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Notes</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Remaining After</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {class_history.map((record) => (
                      <TableRow key={record.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {format(new Date(record.date), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>{getStatusBadge(record.status)}</TableCell>
                        <TableCell className="text-slate-500 text-sm max-w-xs truncate">{record.notes || "-"}</TableCell>
                        <TableCell className="text-right font-medium">{record.remaining_after}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <Card className="mt-6 border-slate-200 shadow-sm" data-testid="payment-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-teal-600" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Package</TableHead>
                  <TableHead className="font-semibold text-center">Credits</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paymentHistory.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell className="text-sm text-slate-600">
                      {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="font-medium">{txn.classes_purchased} Classes</TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                        +{txn.classes_purchased}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{txn.amount} {txn.currency_symbol || "TL"}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        txn.payment_status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                        txn.payment_status === "pending" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                        "bg-slate-100 text-slate-500"
                      }`}>
                        {txn.payment_status === "paid" ? "Paid" : txn.payment_status === "pending" ? "Pending" : txn.payment_status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}


      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => !open && setCancelDialog({ open: false, slot: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Class</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to cancel your class on{" "}
              <strong>{cancelDialog.slot && format(new Date(cancelDialog.slot.date), "EEEE, MMMM d, yyyy")}</strong>{" "}
              at <strong>{cancelDialog.slot?.start_time || cancelDialog.slot?.time}</strong>?
            </p>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-amber-700 text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                <strong>Note:</strong> You can cancel up to 10 minutes before your class starts.
                No credits will be deducted for cancelled classes.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog({ open: false, slot: null })}>Keep Class</Button>
            <Button variant="destructive" onClick={handleCancelClass} disabled={cancelling}>
              {cancelling ? "Cancelling..." : "Yes, Cancel Class"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top Up Credits Dialog */}
      <Dialog open={topUpOpen} onOpenChange={setTopUpOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="topup-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Top Up Credits
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-slate-700 font-medium mb-2">Contact Customer Support</p>
            <p className="text-slate-500 text-sm mb-5">
              Reach us on WhatsApp to purchase more class credits.
            </p>
            <a
              href="https://wa.me/639690740941"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-sm"
              data-testid="whatsapp-contact-link"
            >
              <MessageCircle className="w-5 h-5" />
              +63 9690 740 941
            </a>
            <p className="text-xs text-slate-400 mt-4">We typically respond within a few hours.</p>
          </div>
        </DialogContent>
      </Dialog>
      {/* Reschedule Dialog */}
      <RescheduleDialog
        open={rescheduleDialog.open}
        slot={rescheduleDialog.slot}
        onClose={() => setRescheduleDialog({ open: false, slot: null })}
        onSuccess={() => { setRescheduleDialog({ open: false, slot: null }); fetchDashboard(); }}
        getAuthHeader={getAuthHeader}
      />

      {/* Messaging Dialog */}
      <MessagingDialog
        open={messagingOpen}
        onOpenChange={setMessagingOpen}
        role="student"
        getAuthHeader={getAuthHeader}
      />
    </div>
  );
}

// ───────────────────────────── Reschedule Dialog ─────────────────────────────
function RescheduleDialog({ open, slot, onClose, onSuccess, getAuthHeader }) {
  const [slotsData, setSlotsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [profileId, setProfileId] = useState(null);

  useEffect(() => {
    if (!open || !slot) return;
    setSelectedDate(null);
    setSelectedTime(null);
    setLoading(true);
    // Find the tutor profile_id by user_id
    (async () => {
      try {
        const profilesRes = await axios.get(`/api/tutors/public`);
        const profile = (profilesRes.data.items || []).find((p) => p.user_id === slot.tutor_id);
        if (!profile) {
          toast.error("Tutor profile not found");
          setSlotsData({ slots: [] });
          return;
        }
        setProfileId(profile.id);
        const r = await axios.get(`/api/tutors/public/${profile.id}/available-slots?weeks=4`);
        setSlotsData(r.data);
      } catch {
        setSlotsData({ slots: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, [open, slot]);

  const grouped = (slotsData?.slots || []).reduce((acc, s) => {
    if (s.available) {
      if (!acc[s.date]) acc[s.date] = [];
      acc[s.date].push(s);
    }
    return acc;
  }, {});

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Pick a new date and time");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `/api/tutors/student/reschedule-slot/${slot.id}`,
        { new_date: selectedDate, new_time: selectedTime },
        { headers: getAuthHeader() }
      );
      toast.success("Class rescheduled successfully!");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reschedule failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="text-blue-700 flex items-center gap-2">
            <RefreshCcw className="w-5 h-5" /> Reschedule Class
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 space-y-3">
          {slot && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <p className="text-slate-500">Current slot</p>
              <p className="font-semibold">
                {slot.date} at {slot.start_time || slot.time} with {slot.tutor_name}
              </p>
            </div>
          )}
          <p className="text-xs text-slate-500">
            Your credit stays reserved — pick a new available slot from your tutor's calendar (up to 4 weeks ahead).
          </p>
          {loading ? (
            <div className="flex justify-center py-8">
              <Clock className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : Object.keys(grouped).length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-6">
              No available slots in the next 4 weeks. Please try cancelling instead.
            </p>
          ) : (
            <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2">
              {Object.keys(grouped).sort().map((date) => {
                const d = new Date(date + "T00:00:00");
                return (
                  <div key={date} className="border border-slate-200 rounded-md p-3">
                    <p className="text-sm font-semibold mb-2">
                      {format(d, "EEE, MMM d, yyyy")}
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {grouped[date].map((s) => {
                        const isSelected = selectedDate === date && selectedTime === s.time;
                        return (
                          <button
                            key={`${date}-${s.time}`}
                            onClick={() => { setSelectedDate(date); setSelectedTime(s.time); }}
                            className={cn(
                              "text-xs font-medium py-1.5 px-2 rounded-md transition-all",
                              isSelected
                                ? "bg-blue-600 text-white"
                                : "bg-teal-200 text-slate-900 hover:bg-teal-300"
                            )}
                            data-testid={`resched-slot-${date}-${s.time}`}
                          >
                            {s.time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Keep current</Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedDate} data-testid="confirm-reschedule-btn">
            {submitting ? "Saving..." : "Confirm New Time"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
